import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { logError } from '@/utils/errorUtils'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from './useClient'

export type IdentityVerificationData = Awaited<ReturnType<OrpcClient['getIdentityVerification']>>
export type IdentityStatus = IdentityVerificationData['status']

export function identityVerificationQueryKey(patientId: string | undefined) {
  return ['identity-verification', patientId ?? 'none'] as const
}

/** The patient's identity status for the provider Identity Verification card. */
export function useIdentityVerification(patientId: string | undefined) {
  const client = useClient()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: identityVerificationQueryKey(patientId),
    enabled: !!patientId,
    queryFn: () => client.getIdentityVerification({ id: patientId! }),
  })

  useEffect(() => {
    if (query.error) {
      logError('patient', 'Failed to load identity verification', query.error)
    }
  }, [query.error])

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: identityVerificationQueryKey(patientId) }),
    [queryClient, patientId],
  )

  return { data: query.data ?? null, loading: query.isLoading, refresh }
}
